/**
 * Кастомний елемент <localization-form>.
 *
 * Використовується у двох різних сценаріях:
 *   1) шухляда вибору країни (з пошуком, оверлеєм, на всю висоту) — мобільна;
 *   2) невеликий випадаючий список мов у хедері — десктоп і мобайл.
 *
 * Ключова відмінність від оригіналу: блокування скролу сторінки
 * (клас overflow-hidden-mobile на <body>) вмикається ТІЛЬКИ для сценарію (1).
 * Для селектора мов у хедері воно ламало sticky-хедер: overflow на <body>
 * робить його окремим скролпортом, і position: sticky всередині перестає діяти.
 */
if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();

        this.mql = window.matchMedia('(min-width: 750px)');
        this.stickyHeader = this.closest('sticky-header');

        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('#sr-country-search-results'),
        };

        if (!this.elements.button || !this.elements.panel) return;

        this.onReposition = this.positionPanel.bind(this);

        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.elements.button.addEventListener('click', this.openSelector.bind(this));

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }

        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', this.hidePanel.bind(this));
        }

        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
      }

      /* ------------------------------------------------------------------ */
      /* Сценарій                                                            */
      /* ------------------------------------------------------------------ */

      /** Компактний випадаючий список у хедері (мови / валюта). */
      get isHeaderDropdown() {
        return !!this.closest(
          '.dj-classic-header__language, .header-localization, .desktop-localization-wrapper, sticky-header'
        );
      }

      /** Повноекранна шухляда вибору країни — саме їй потрібне блокування скролу. */
      get isCountryDrawer() {
        return !this.isHeaderDropdown && !!this.querySelector('.country-selector');
      }

      /* ------------------------------------------------------------------ */
      /* Sticky-хедер                                                        */
      /* ------------------------------------------------------------------ */

      /**
       * Поки список відкритий, хедер не має ховатися при скролі.
       * Позиціонування хедера НЕ чіпаємо — цим займається сама тема.
       */
      lockStickyHeader() {
        if (!this.stickyHeader) return;
        this.stickyHeader.preventHide = true;
        this.stickyHeader.classList.remove('shopify-section-header-hidden');
        document.querySelector('.section-header')?.classList.remove('shopify-section-header-hidden');
      }

      unlockStickyHeader() {
        if (!this.stickyHeader) return;
        this.stickyHeader.preventHide = false;
      }

      /* ------------------------------------------------------------------ */
      /* Позиціонування панелі                                               */
      /* ------------------------------------------------------------------ */

      /**
       * Панель у хедері виводиться як fixed відносно кнопки, щоб її не обрізали
       * контейнери хедера з overflow. Перераховується при скролі та ресайзі.
       */
      positionPanel() {
        if (!this.isHeaderDropdown) return;

        const rect = this.elements.button.getBoundingClientRect();
        const isRtl = document.documentElement.dir === 'rtl';
        const style = this.elements.panel.style;

        style.setProperty('position', 'fixed', 'important');
        style.setProperty('top', `${Math.round(rect.bottom + 8)}px`, 'important');
        style.setProperty('bottom', 'auto', 'important');
        style.setProperty('transform', 'none', 'important');

        if (isRtl) {
          style.setProperty('left', `${Math.max(8, Math.round(rect.left))}px`, 'important');
          style.setProperty('right', 'auto', 'important');
        } else {
          style.setProperty('right', `${Math.max(8, Math.round(window.innerWidth - rect.right))}px`, 'important');
          style.setProperty('left', 'auto', 'important');
        }
      }

      clearPanelPosition() {
        ['position', 'inset', 'top', 'right', 'bottom', 'left', 'transform'].forEach((prop) =>
          this.elements.panel.style.removeProperty(prop)
        );
      }

      /* ------------------------------------------------------------------ */
      /* Відкриття / закриття                                                */
      /* ------------------------------------------------------------------ */

      openSelector(event) {
        event?.preventDefault?.();
        event?.stopPropagation?.();

        const isOpen = this.elements.button.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
          this.hidePanel();
          return;
        }

        this.elements.button.focus();
        this.lockStickyHeader();

        this.elements.panel.removeAttribute('hidden');
        this.elements.panel.classList.add('dj-header-language-panel-open');
        this.elements.button.setAttribute('aria-expanded', 'true');

        if (this.isHeaderDropdown) {
          this.positionPanel();
          window.addEventListener('scroll', this.onReposition, { passive: true });
          window.addEventListener('resize', this.onReposition);
        }

        /* Блокування скролу сторінки — лише для повноекранної шухляди країн.
           Для хедерного списку це ламає sticky-позиціонування. */
        if (this.isCountryDrawer) {
          document.body.classList.add('overflow-hidden-mobile');
          document.querySelector('.menu-drawer')?.classList.add('country-selector-open');
        }

        if (this.elements.search && this.mql.matches) this.elements.search.focus();
      }

      hidePanel() {
        if (!this.elements.button || !this.elements.panel) return;

        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
        this.elements.panel.classList.remove('dj-header-language-panel-open');

        window.removeEventListener('scroll', this.onReposition);
        window.removeEventListener('resize', this.onReposition);
        this.clearPanelPosition();

        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.setAttribute('aria-activedescendant', '');
        }

        if (this.isCountryDrawer) {
          document.body.classList.remove('overflow-hidden-mobile');
          document.querySelector('.menu-drawer')?.classList.remove('country-selector-open');
        }

        this.unlockStickyHeader();
      }

      closeSelector(event) {
        const leftComponent = !this.contains(event.target) || !this.contains(event.relatedTarget);
        if (event.target.classList.contains('country-selector__overlay') || leftComponent) this.hidePanel();
      }

      /* ------------------------------------------------------------------ */
      /* Клавіатура                                                          */
      /* ------------------------------------------------------------------ */

      onContainerKeyDown(event) {
        const focusableItems = Array.from(this.querySelectorAll('a')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus?.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus?.focus();
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          this.elements.search.setAttribute(
            'aria-activedescendant',
            focusedItemIndex > -1 ? focusableItems[focusedItemIndex].id : ''
          );
        });
      }

      onContainerKeyUp(event) {
        event.preventDefault();

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (this.elements.button.getAttribute('aria-expanded') === 'false') return;
            this.hidePanel();
            event.stopPropagation();
            this.elements.button.focus();
            break;
          case 'SPACE':
            if (this.elements.button.getAttribute('aria-expanded') === 'true') return;
            this.openSelector(event);
            break;
        }
      }

      onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        if (this.elements.input) this.elements.input.value = event.currentTarget.dataset.value;
        if (form) form.submit();
      }

      /* ------------------------------------------------------------------ */
      /* Пошук по країнах                                                    */
      /* ------------------------------------------------------------------ */

      normalizeString(str) {
        return str.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
      }

      filterCountries() {
        if (!this.elements.search) return;

        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = this.querySelectorAll('a');
        let visibleCountries = allCountries.length;

        this.elements.resetButton?.classList.toggle('hidden', !searchValue);
        if (popularCountries) popularCountries.classList.toggle('hidden', searchValue);

        allCountries.forEach((item) => {
          const countryName = this.normalizeString(item.querySelector('.country')?.textContent || item.textContent || '');
          if (countryName.indexOf(searchValue) > -1) {
            item.parentElement.classList.remove('hidden');
            visibleCountries++;
          } else {
            item.parentElement.classList.add('hidden');
            visibleCountries--;
          }
        });

        if (this.elements.liveRegion && window.accessibilityStrings?.countrySelectorSearchCount) {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          );
        }

        this.querySelector('.country-selector')?.scrollTo(0, 0);
        this.querySelector('.country-selector__list')?.scrollTo(0, 0);
      }

      resetFilter(event) {
        event.stopPropagation();
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon?.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search.value) {
          this.elements.searchIcon?.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') event.preventDefault();
      }
    }
  );
}